import { useQuery } from "react-query";
import { axiosAuth, axiosNoAuth } from "./axiosConfig";

// DRY helpers and shared config
const CONNECTOR_BASE = process.env.REACT_APP_CONNECTOR_ROUTE || "";

/**
 * @param {string} path
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
const getConnector = (path) => axiosNoAuth.get(`${CONNECTOR_BASE}${path}`);

/**
 * @param {string} path
 * @param {any} body
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
const postConnector = (path, body) =>
  axiosNoAuth.post(`${CONNECTOR_BASE}${path}`, body);

/**
 * @param {string} path
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
const getServer = (path) => axiosAuth.get(path);

/**
 * @param {string} path
 * @param {any} body
 * @returns {Promise<import('axios').AxiosResponse<any>>}
 */
const postServer = (path, body) => axiosAuth.post(path, body);

/**
 * Normalize axios errors into a common shape
 * @param {any} err
 */
const toErrorInfo = (err) => ({
  status: err?.response?.status || 500,
  message:
    err?.response?.data?.error?.message ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed",
});

/**
 * Safe, minimal logger for axios errors
 * @param {string} ctx
 * @param {any} err
 */
const logAxiosError = (ctx, err) => {
  const info = toErrorInfo(err);
  // eslint-disable-next-line no-console
  console.error(`[${ctx}] ${info.status}: ${info.message}`);
};

/**
 * Health of the connector app
 * @param {(data:any)=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useConnectorAppHealth = (successFunc, errorLogger) => {
  return useQuery(
    ["connectorAppHealth", CONNECTOR_BASE],
    async () => {
      try {
        const response = await getConnector("/health");
        // Normalize the response to always have a healthy field
        const data = response.data || {};
        return {
          healthy: data.status === "running" || response.status === 200,
          healthSupported: true,
          status: response.status,
          message: data.message || "Connector app is healthy",
          ...data,
        };
      } catch (err) {
        // Backwards compatibility: older connector app versions may not have /health (404)
        if (err.response?.status === 404) {
          return {
            healthy: true, // Assume operational so rest of flow can proceed
            healthSupported: false,
            status: 404,
            message:
              "Legacy Connector App: health check not supported. Please update for monitoring.",
          };
        }
        const info = toErrorInfo(err);
        return {
          healthy: false,
          status: info.status || 503,
          message: info.message || "Connector app is not reachable",
        };
      }
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};

/**
 * Fetch connector devices.
 * @param {{healthy?:boolean,status?:number,healthSupported?:boolean}} healthData
 * @param {(data:any)=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useConnectorDevices = (healthData, successFunc, errorLogger) => {
  return useQuery(
    ["connectorDevices", CONNECTOR_BASE],
    async () => {
      try {
        const response = await getConnector("/scan-devices");
        // Extract devices from response (support both legacy and new shapes)
        const devices =
          response.data?.data?.devices || response.data?.devices || [];
        return {
          status: response.status,
          devices: devices.map((device) => device.serial),
          message:
            devices.length === 0
              ? "No devices found"
              : `Found ${devices.length} device(s)`,
        };
      } catch (err) {
        const info = toErrorInfo(err);
        // Log here minimally; also surfaces via onError
        logAxiosError("connectorDevices", err);
        return {
          status: info.status,
          devices: [],
          message: info.message || "Error scanning devices",
        };
      }
    },
    {
      // Enable if health reports healthy OR legacy (404 health unsupported) OR explicitly marked unsupported
      enabled:
        healthData?.healthy === true ||
        healthData?.status === 404 ||
        healthData?.healthSupported === false,
      refetchOnWindowFocus: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};
/**
 * Fetch server devices by serial IDs.
 * @param {string[]|undefined} deviceSerials
 * @param {(data:any[])=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useServerDevices = (deviceSerials, successFunc, errorLogger) => {
  const keySerials = Array.isArray(deviceSerials)
    ? [...deviceSerials].sort()
    : [];
  return useQuery(
    ["serverDevices", keySerials],
    async () => {
      if (!deviceSerials || deviceSerials.length === 0) {
        return [];
      }

      const ids = deviceSerials;
      const devices = [];

      await Promise.all(
        ids.map(async (id) => {
          try {
            const result = await getServer(`/devices/${id}`);
            devices.push(result.data);
          } catch (err) {
            logAxiosError(
              `serverDevices: could not retrieve device serial ${id}`,
              err
            );
          }
        })
      );
      return devices;
    },
    {
      enabled: Array.isArray(deviceSerials) && deviceSerials.length > 0,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};
/**
 * Calibrate connector device.
 * @param {string} id
 * @param {(data:any)=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useConnectorCalibrate = (id, successFunc, errorLogger) => {
  return useQuery(
    ["connectorCalibrate", id],
    async () => {
      let response = {};
      try {
        let lastCalibrationScan = null;
        try {
          const lastCalibrationResponse = await getServer(
            `/devices/get-last-calibration-scan/${id}`
          );
          lastCalibrationScan = lastCalibrationResponse.data || null;
        } catch (scanErr) {
          // If no calibration scan found (404), continue with null
          if (scanErr?.response?.status === 404) {
            console.log(
              `No previous calibration scan found for device ${id}, continuing with new calibration`
            );
          } else {
            // Non-404 errors (DB issues, permissions, network) shouldn't block calibration
            // Log the error but proceed with null to allow calibration to succeed
            console.warn(
              `Failed to fetch last calibration scan for device ${id}:`,
              scanErr?.response?.status || scanErr.message,
              "- proceeding without previous scan data"
            );
          }
          lastCalibrationScan = null;
        }

        const calibrationResponse = await postConnector("/calibrate-device", {
          deviceSerial: id,
          lastCalibrationScan: lastCalibrationScan,
          scanType: "trace", //needed for legacy support, new connector apps ignore this field
        });

        const data = calibrationResponse.data || {};
        let saveResponse;
        try {
          saveResponse = await postServer("/devices/save-calibration-scan", {
            ...data,
            ...(data.deviceSerial === undefined ? { deviceSerial: id } : {}),
          });
        } catch (saveErr) {
          // Save failed, but calibration succeeded
          const saveErrorInfo = toErrorInfo(saveErr);
          saveResponse = {
            status: saveErrorInfo.status || 500,
            message:
              saveErrorInfo.message ||
              "Calibration succeeded, but saving the result failed.",
          };
        }
        // Ensure overall status reflects the calibration request outcome.
        // Standardize shape: include nested save result consistently.
        response = {
          status: calibrationResponse.status,
          save: {
            status:
              typeof saveResponse?.status === "number"
                ? saveResponse.status
                : saveResponse?.statusCode || saveResponse?.statusText
                ? 200
                : 200,
            message: saveResponse?.message,
            data: saveResponse?.data,
          },
        };
      } catch (err) {
        console.log("Error during calibration:", err);
        const info = toErrorInfo(err);
        response.status = info.status || 400;
        response.message = info.message || "Error calibrating device.";

        if (err.response?.data) {
          try {
            const saveResponse = await postServer(
              "/devices/save-calibration-scan",
              {
                ...err.response?.data,
                ...(err.response?.data?.deviceSerial === undefined
                  ? { deviceSerial: id }
                  : {}),
              }
            );

            response = {
              status: response.status,
              message: response.message,
              save: {
                status:
                  typeof saveResponse?.status === "number"
                    ? saveResponse.status
                    : 201,
                data: saveResponse?.data,
              },
            };
          } catch (saveErr) {
            console.log("Error saving failed calibration scan:", saveErr);
            const saveErrorInfo = toErrorInfo(saveErr);
            response.save = {
              status: saveErrorInfo.status || 500,
              message:
                saveErrorInfo.message ||
                "Failed to save calibration error for troubleshooting.",
            };
          }
        }
      }
      return response;
    },
    {
      enabled: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};
/**
 * Calibrate server device.
 * @param {string} id
 * @param {string} date
 * @param {(data:any)=>void} [successFunc]
 * @param {(err:any)=>void} [errorLogger]
 */
export const useServerCalibrate = (id, date, successFunc, errorLogger) => {
  return useQuery(
    ["serverCalibrate", id],
    async () => {
      let response = {};
      try {
        response = await axiosAuth.patch(`/devices/${id}`, {
          lastCalibration: date,
        });
      } catch (err) {
        const info = toErrorInfo(err);
        logAxiosError("serverCalibrate: patch calibration", err);
        response.status = info.status || 400;
        response.message =
          info.message || "Error saving calibration date to server.";
      }
      return response;
    },
    {
      enabled: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};
/**
 * Get measurement trace from connector device.
 * @param {string} deviceSerial
 * @param {"trace"|"bulk"|string} scanType
 * @param {(err:any)=>void} [errorLogger]
 */
export const useGetMeasurementTrace = (deviceSerial, scanType, errorLogger) => {
  return useQuery(
    ["getMeasurementTrace", deviceSerial, scanType],
    async () => {
      try {
        const { data } = await postConnector("/get-trace-measurement", {
          deviceSerial,
          scanType,
        });
        return data;
      } catch (err) {
        logAxiosError("getMeasurementTrace", err);
        throw err;
      }
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    }
  );
};
/**
 * Get bulk measurement from connector device.
 * @param {string} deviceSerial
 * @param {"bulk"|"trace"|string} scanType
 * @param {(err:any)=>void} [errorLogger]
 */
export const useGetMeasurementBulk = (deviceSerial, scanType, errorLogger) => {
  return useQuery(
    ["getMeasurementBulk", deviceSerial, scanType],
    async () => {
      try {
        const { data } = await postConnector("/get-bulk-measurement", {
          deviceSerial,
          scanType,
        });
        return data;
      } catch (err) {
        logAxiosError("getMeasurementBulk", err);
        throw err;
      }
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    }
  );
};
